import { Checkbox } from '@timelyapp/react';
import DropdownListItem from 'components/DropdownListItem';
import { Info } from 'components/Icons';
import RichTooltip from 'components/RichTooltip';
import { RadioButton } from 'components/radio-new/RadioButton';
import RadioGroup from 'components/radio-new/RadioGroup';
import React from 'react';
import { useDispatch } from 'react-redux';
import type { WidgetOptions, WidgetWithData } from 'redux/modules/superreports.types';
import {
  updateWidgetOptions,
  WidgetBarScale,
  WidgetDataType,
} from 'redux/modules/superreports.types';
import type { Features } from 'types/account';
import type { ThunkDispatch } from 'types/redux';

interface Props {
  features: Features;
  widget: WidgetWithData;
}

const TimelineWidgetOptions: React.FC<Props> = ({ widget }) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const handleChangeOption = <TOption extends keyof WidgetOptions>(
    option: TOption,
    value: WidgetOptions[TOption]
  ) => {
    dispatch(
      updateWidgetOptions({
        reference: widget.id || widget._ref,
        options: {
          [option]: value,
        },
      })
    );
  };

  const barScale = Object.values(WidgetBarScale).includes(
    widget.options?.barScale as WidgetBarScale
  )
    ? widget.options!.barScale
    : WidgetBarScale.Default;

  return (
    <>
      <DropdownListItem type="header">Bar scale</DropdownListItem>
      <RadioGroup
        style={{ display: 'content' }}
        selectedValue={barScale}
        onChangeSelected={value => handleChangeOption('barScale', value as WidgetBarScale)}
      >
        {Object.entries(WidgetBarScale).map(([label, value]) => (
          <DropdownListItem
            key={value}
            onClick={() => handleChangeOption('barScale', value as WidgetBarScale)}
          >
            <RadioButton id={`barScale_${value}`} value={value} label={label} />
            {value === WidgetBarScale.Default && (
              <>
                <RichTooltip
                  fixed
                  tooltip="The optimal view for displaying your data, based on the time range selected."
                  style={{ display: 'inline-flex', marginLeft: 10 }}
                  position="top"
                >
                  <Info style={{ width: 12, height: 12, opacity: 0.5 }} />
                </RichTooltip>
              </>
            )}
          </DropdownListItem>
        ))}
      </RadioGroup>
      <DropdownListItem type="divider" />
      <DropdownListItem
        onClick={() => handleChangeOption('showMedian', !widget.options?.showMedian)}
      >
        Display median line
        <Checkbox
          style={{ marginLeft: 'auto' }}
          checked={!!widget.options?.showMedian}
          onChangeChecked={checked => handleChangeOption('showMedian', checked)}
        />
      </DropdownListItem>
      {widget.options?.dataType === WidgetDataType.Hours && (
        <DropdownListItem
          onClick={() =>
            handleChangeOption('showBillableNonBillable', !widget.options?.showBillableNonBillable)
          }
        >
          Show billable/non-billable
          <Checkbox
            style={{ marginLeft: 'auto' }}
            checked={!!widget.options?.showBillableNonBillable}
            onChangeChecked={checked => handleChangeOption('showBillableNonBillable', checked)}
          />
        </DropdownListItem>
      )}
    </>
  );
};

export default TimelineWidgetOptions;
